// What a Pokémon battery save says about itself: trainer name, time played
// and Pokédex count. Offsets are the ones PKHeX uses (SAV1Offsets,
// SAV2Offsets, SAV3) for international carts; a save that doesn't parse
// plausibly yields null rather than nonsense.

export type GameKind = "gen1" | "gen2-gs" | "gen2-crystal" | "gen3";

export interface SaveStats {
  trainer: string | null;
  playTime: { hours: number; minutes: number } | null;
  dexOwned: number | null;
}

// Game Boy header title → which save layout to read.
export function gameKindFromTitle(title: string): GameKind | null {
  if (/^POKEMON (RED|BLUE|YELLOW)/.test(title)) return "gen1";
  if (/^POKEMON_(GLD|SLV)/.test(title)) return "gen2-gs";
  if (/^PM_CRYSTAL/.test(title)) return "gen2-crystal";
  return null;
}

/** The cartridge title from a Game Boy header: up to 15 bytes at 0x134 (0x143 is the Color flag), NUL-padded. */
export function gameBoyTitle(rom: Uint8Array): string {
  return String.fromCharCode(...rom.subarray(0x134, 0x143)).replace(/\0.*$/, "");
}

function decodeGen12(bytes: Uint8Array, max: number): string {
  let out = "";
  for (let i = 0; i < max; i++) {
    const b = bytes[i];
    if (b === 0x50 || b === undefined) break;
    if (b >= 0x80 && b <= 0x99) out += String.fromCharCode(65 + b - 0x80);
    else if (b >= 0xa0 && b <= 0xb9) out += String.fromCharCode(97 + b - 0xa0);
    else if (b >= 0xf6 && b <= 0xff) out += String.fromCharCode(48 + b - 0xf6);
    else if (b === 0x7f) out += " ";
    else out += "?";
  }
  return out.trim();
}

function decodeGen3(bytes: Uint8Array, max: number): string {
  let out = "";
  for (let i = 0; i < max; i++) {
    const b = bytes[i];
    if (b === 0xff || b === undefined) break;
    if (b >= 0xbb && b <= 0xd4) out += String.fromCharCode(65 + b - 0xbb);
    else if (b >= 0xd5 && b <= 0xee) out += String.fromCharCode(97 + b - 0xd5);
    else if (b >= 0xa1 && b <= 0xaa) out += String.fromCharCode(48 + b - 0xa1);
    else if (b === 0x00) out += " ";
    else out += "?";
  }
  return out.trim();
}

function countBits(bytes: Uint8Array, limit: number): number {
  let n = 0;
  for (let bit = 0; bit < limit; bit++) if (bytes[bit >> 3] & (1 << (bit & 7))) n++;
  return n;
}

function plausible(hours: number, minutes: number, seconds: number) {
  return hours < 1000 && minutes < 60 && seconds < 60;
}

const GEN3_SIGNATURE = 0x08012025;
const GEN3_SECTOR = 0x1000;
const GEN3_SECTORS = 14;

// The newer of the two 14-sector blocks, as (sector base of section 0).
function gen3Section0(save: Uint8Array): number | null {
  const view = new DataView(save.buffer, save.byteOffset, save.byteLength);
  let best: { index: number; section0: number } | null = null;
  for (const block of [0, 1]) {
    const base = block * GEN3_SECTORS * GEN3_SECTOR;
    if (base + GEN3_SECTORS * GEN3_SECTOR > save.length) break;
    let section0 = -1;
    let index = -1;
    let valid = true;
    for (let i = 0; i < GEN3_SECTORS; i++) {
      const sector = base + i * GEN3_SECTOR;
      if (view.getUint32(sector + 0xff8, true) !== GEN3_SIGNATURE) {
        valid = false;
        break;
      }
      index = view.getUint32(sector + 0xffc, true);
      if (view.getUint16(sector + 0xff4, true) === 0) section0 = sector;
    }
    if (!valid || section0 < 0) continue;
    if (!best || index > best.index) best = { index, section0 };
  }
  return best?.section0 ?? null;
}

export function parseSaveStats(save: Uint8Array, kind: GameKind): SaveStats | null {
  if (kind === "gen3") {
    const base = gen3Section0(save);
    if (base === null) return null;
    const view = new DataView(save.buffer, save.byteOffset, save.byteLength);
    const hours = view.getUint16(base + 0x0e, true);
    const minutes = save[base + 0x10];
    const seconds = save[base + 0x11];
    if (!plausible(hours, minutes, seconds)) return null;
    return {
      trainer: decodeGen3(save.subarray(base, base + 7), 7) || null,
      playTime: { hours, minutes },
      dexOwned: countBits(save.subarray(base + 0x28, base + 0x28 + 49), 386),
    };
  }
  if (kind === "gen1") {
    if (save.length < 0x2cf1) return null;
    const hours = save[0x2ced];
    const minutes = save[0x2cef];
    const seconds = save[0x2cf0];
    if (!plausible(hours, minutes, seconds)) return null;
    return {
      trainer: decodeGen12(save.subarray(0x2598, 0x25a3), 11) || null,
      playTime: { hours, minutes },
      dexOwned: countBits(save.subarray(0x25a3, 0x25a3 + 19), 151),
    };
  }
  const time = kind === "gen2-gs" ? 0x2053 : 0x2052;
  const dex = kind === "gen2-gs" ? 0x2a4c : 0x2a27;
  if (save.length < dex + 32) return null;
  const hours = (save[time] << 8) | save[time + 1];
  const minutes = save[time + 2];
  const seconds = save[time + 3];
  if (!plausible(hours, minutes, seconds)) return null;
  return {
    trainer: decodeGen12(save.subarray(0x200b, 0x2016), 11) || null,
    playTime: { hours, minutes },
    dexOwned: countBits(save.subarray(dex, dex + 32), 251),
  };
}

export function formatPlayTime(time: { hours: number; minutes: number }): string {
  return `${time.hours}h ${String(time.minutes).padStart(2, "0")}m`;
}
